'use client';

import { useSyncExternalStore } from 'react';

export type AiSearchPhase = 'idle' | 'interpreting' | 'loading' | 'done' | 'error';

export interface AiSearchProgress {
  phase: AiSearchPhase;
  query: string;
  startedAt: number;
  targetKey?: string;
}

const idle: AiSearchProgress = { phase: 'idle', query: '', startedAt: 0 };
const MAX_PROGRESS_AGE_MS = 20_000;
let current: AiSearchProgress = idle;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

export function getAiSearchProgress(): AiSearchProgress {
  return current;
}

export function setAiSearchProgress(progress: AiSearchProgress): void {
  if (safetyTimer !== null) clearTimeout(safetyTimer);
  current = progress;
  listeners.forEach((listener) => listener());
  if (progress.phase !== 'idle') {
    const remaining = Math.max(0, MAX_PROGRESS_AGE_MS - (Date.now() - progress.startedAt));
    safetyTimer = setTimeout(() => {
      if (current === progress) setAiSearchProgress(idle);
    }, remaining);
  } else {
    safetyTimer = null;
  }
}

export function resetAiSearchProgress(phase: 'done' | 'error' = 'done'): void {
  setAiSearchProgress({ ...current, phase });
  setAiSearchProgress(idle);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAiSearchProgress(): AiSearchProgress {
  return useSyncExternalStore(subscribe, getAiSearchProgress, () => idle);
}
